import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { 
  ShieldAlert, 
  Lock, 
  Eye, 
  Fingerprint, 
  AlertTriangle,
  FileText,
  Video,
  CheckCircle,
  Phone,
  Mail,
  MapPin,
  ChevronDown,
  Users,
  Target,
  Zap,
  DollarSign
} from "lucide-react";
import Jobs from "@/pages/Jobs";
import JobPosting from "@/pages/JobPosting";
import JobGuatemala from "@/pages/JobGuatemala";
import JobUkraine from "@/pages/JobUkraine";
import JobPhilippines from "@/pages/JobPhilippines";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Images from design guidelines and searches
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1769980084959-6b32a5f1b6ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBsb2JieSUyMG5pZ2h0JTIwZGFyayUyMG1vb2R5fGVufDB8fHx8MTc3MDE3NjEwNnww&ixlib=rb-4.1.0&q=85",
  guard: "https://images.unsplash.com/photo-1765741836920-421cb507d7c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkJTIwY2hlY2twb2ludCUyMG5pZ2h0fGVufDB8fHx8MTc3MDE3NjIwM3ww&ixlib=rb-4.1.0&q=85",
  lock: "https://images.unsplash.com/photo-1641452809210-0923d1996174?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjBvcGVuaW5nJTIwbG9ja2VkJTIwZG9vciUyMHdpdGglMjB0b29scyUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzcwMTc2MTA5fDA&ixlib=rb-4.1.0&q=85",
  badge: "https://images.unsplash.com/photo-1609771377023-7a7480af949a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGJhZGdlJTIwbGFueWFyZCUyMG9uJTIwZGVzayUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzcwMTc2MTI2fDA&ixlib=rb-4.1.0&q=85",
  tactical: "https://images.unsplash.com/photo-1582461127767-571db9b124ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjBpbiUyMGJsYWNrJTIwdGFjdGljYWwlMjBnZWFyJTIwd2Fsa2luZyUyMGF3YXklMjBkYXJrfGVufDB8fHx8MTc3MDE3NjEzMXww&ixlib=rb-4.1.0&q=85",
  shadow: "https://images.unsplash.com/photo-1668971877896-9407fef4944b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxob29kZWQlMjBmaWd1cmUlMjBkYXJrJTIwc3VydmVpbGxhbmNlJTIwc2hhZG93fGVufDB8fHx8MTc3MDE3NjE5OHww&ixlib=rb-4.1.0&q=85",
  door: "https://images.unsplash.com/photo-1678524492368-240e9df021df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHx0YWlsZ2F0aW5nJTIwc2VjdXJpdHklMjBkb29yJTIwYWNjZXNzJTIwY29ycG9yYXRlfGVufDB8fHx8MTc3MDE3NjE4OHww&ixlib=rb-4.1.0&q=85",
};

function Home() {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    security_concern: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/consultation`, formData);
      setSubmitStatus("success");
      setFormData({ name: "", company: "", email: "", phone: "", security_concern: "" });
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-sm border-b border-white/10">
        <div className="section-container flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
            <span 
              className="font-bold text-xl tracking-tight uppercase"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="logo-text"
            >
              Simon Advanced Security
            </span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('problem')} 
              className="footer-link hover:text-orange-500"
              data-testid="nav-problem"
            >
              The Problem
            </button>
            <button 
              onClick={() => scrollToSection('services')} 
              className="footer-link hover:text-orange-500"
              data-testid="nav-services"
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection('results')} 
              className="footer-link hover:text-orange-500"
              data-testid="nav-results"
            >
              Results
            </button>
            <Link 
              to="/jobs" 
              className="footer-link hover:text-orange-500"
              data-testid="nav-careers"
            >
              Careers
            </Link>
            <button 
              onClick={() => scrollToSection('contact')} 
              className="btn-primary text-sm py-2 px-4"
              data-testid="nav-contact-btn"
            >
              Request Audit
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section relative flex items-center" data-testid="hero-section">
        <div 
          className="hero-bg"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        />
        <div className="hero-overlay" />
        
        <div className="section-container relative z-10 py-32 md:py-0">
          <div className="max-w-4xl">
            <p 
              className="text-orange-500 text-xs md:text-sm tracking-[0.3em] uppercase mb-4 fade-in-up"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              Physical Penetration Testing
            </p>
            <h1 
              className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter uppercase text-white mb-6 fade-in-up delay-100"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="hero-headline"
            >
              We Break In<br />
              <span className="text-orange-500">So They Can't</span>
            </h1>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mb-8 leading-relaxed fade-in-up delay-200">
              Simon Advanced Security Checkers LLC. The ethical hackers of the real world. 
              We find vulnerabilities in your people, your procedures, and your perimeter 
              before criminals do.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 fade-in-up delay-300">
              <button 
                onClick={() => scrollToSection('contact')}
                className="btn-primary flex items-center justify-center gap-2"
                data-testid="hero-cta-btn"
              >
                <ShieldAlert className="w-5 h-5" />
                Request Audit
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className="btn-secondary flex items-center justify-center gap-2"
                data-testid="hero-learn-more-btn"
              >
                Learn More
                <ChevronDown className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <ChevronDown className="w-6 h-6 text-orange-500" />
        </div>
      </section>

      {/* Problem Section */}
      <section id="problem" className="py-24 bg-[#0A0A0A]" data-testid="problem-section">
        <div className="section-container">
          <div className="text-center mb-16">
            <p 
              className="text-orange-500 text-xs tracking-[0.3em] uppercase mb-4"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              The Uncomfortable Truth
            </p>
            <h2 
              className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="problem-headline"
            >
              Your Human Firewall Has Holes
            </h2>
            <p className="text-gray-400 max-w-3xl mx-auto text-base md:text-lg">
              You invest millions in surveillance systems, access controls, and cybersecurity. 
              But what is the real-world value if your human firewall can be bypassed with a fifty-dollar bill?
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="problem-card" data-testid="problem-card-1">
              <div className="w-16 h-16 bg-orange-500/10 flex items-center justify-center mb-6 mx-auto">
                <Users className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
              </div>
              <h3 
                className="text-xl font-bold uppercase tracking-tight text-white mb-3"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Guards Get Distracted
              </h3>
              <p className="text-gray-400 text-sm">
                A security guard on their phone, a moment of inattention—your perimeter 
                becomes vulnerable in seconds.
              </p>
            </div>

            <div className="problem-card" data-testid="problem-card-2">
              <div className="w-16 h-16 bg-orange-500/10 flex items-center justify-center mb-6 mx-auto">
                <AlertTriangle className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
              </div>
              <h3 
                className="text-xl font-bold uppercase tracking-tight text-white mb-3"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Procedures Get Ignored
              </h3>
              <p className="text-gray-400 text-sm">
                A poorly understood checkpoint procedure or badge verification can render 
                your entire security system obsolete.
              </p>
            </div>

            <div className="problem-card" data-testid="problem-card-3">
              <div className="w-16 h-16 bg-orange-500/10 flex items-center justify-center mb-6 mx-auto">
                <Target className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
              </div>
              <h3 
                className="text-xl font-bold uppercase tracking-tight text-white mb-3"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                People Are Targetable
              </h3>
              <p className="text-gray-400 text-sm">
                An employee susceptible to a simple bribe or social engineering can bypass 
                your multi-million dollar security investment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-[#121212]" data-testid="services-section">
        <div className="section-container">
          <div className="text-center mb-16">
            <p 
              className="text-orange-500 text-xs tracking-[0.3em] uppercase mb-4"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              Our Methodology
            </p>
            <h2 
              className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="services-headline"
            >
              Human Firewall Audit
            </h2>
            <p className="text-gray-400 max-w-3xl mx-auto text-base md:text-lg">
              We don't just review security protocols on paper. We test them in the field 
              with the same ingenuity and tactics that real-world threat actors use.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Service Card 1 */}
            <div className="service-card group" data-testid="service-card-1">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-orange-500/10 flex-shrink-0 flex items-center justify-center">
                  <Eye className="w-10 h-10 text-orange-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 
                    className="text-2xl font-bold uppercase tracking-tight text-white mb-3"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    Guard Vigilance Testing
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Our operatives will attempt to breach your perimeter using sophisticated 
                    social engineering and pretexts to test guard alertness and response.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-xs text-gray-500">
                      <Zap className="w-4 h-4 text-orange-500" />
                      Distraction techniques
                    </li>
                    <li className="flex items-center gap-2 text-xs text-gray-500">
                      <Zap className="w-4 h-4 text-orange-500" />
                      Shift change vulnerabilities
                    </li>
                    <li className="flex items-center gap-2 text-xs text-gray-500">
                      <Zap className="w-4 h-4 text-orange-500" />
                      Response time assessment
                    </li>
                  </ul>
                </div>
              </div>
              <img 
                src={IMAGES.guard} 
                alt="Security guard checkpoint" 
                className="w-full h-48 object-cover mt-6 opacity-60 group-hover:opacity-80 transition-opacity"
              />
            </div>

            {/* Service Card 2 */}
            <div className="service-card group" data-testid="service-card-2">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-orange-500/10 flex-shrink-0 flex items-center justify-center">
                  <Lock className="w-10 h-10 text-orange-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 
                    className="text-2xl font-bold uppercase tracking-tight text-white mb-3"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    Physical Penetration
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    We test your physical barriers—locks, doors, access points—using the same 
                    methods criminals employ to find weaknesses.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-xs text-gray-500">
                      <Zap className="w-4 h-4 text-orange-500" />
                      Lock bypass techniques
                    </li>
                    <li className="flex items-center gap-2 text-xs text-gray-500">
                      <Zap className="w-4 h-4 text-orange-500" />
                      Sensor evasion
                    </li>
                    <li className="flex items-center gap-2 text-xs text-gray-500">
                      <Zap className="w-4 h-4 text-orange-500" />
                      Access control exploits
                    </li>
                  </ul>
                </div>
              </div>
              <img 
                src={IMAGES.lock} 
                alt="Lock picking" 
                className="w-full h-48 object-cover mt-6 opacity-60 group-hover:opacity-80 transition-opacity"
              />
            </div>

            {/* Service Card 3 */}
            <div className="service-card group" data-testid="service-card-3">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-orange-500/10 flex-shrink-0 flex items-center justify-center">
                  <Fingerprint className="w-10 h-10 text-orange-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 
                    className="text-2xl font-bold uppercase tracking-tight text-white mb-3"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    Bribery & Coercion Testing
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    In controlled, ethical scenarios, we test if personnel can be influenced 
                    through monetary offers or social pressure to bypass protocols.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-xs text-gray-500">
                      <Zap className="w-4 h-4 text-orange-500" />
                      Monetary incentive tests
                    </li>
                    <li className="flex items-center gap-2 text-xs text-gray-500">
                      <Zap className="w-4 h-4 text-orange-500" />
                      Authority impersonation
                    </li>
                    <li className="flex items-center gap-2 text-xs text-gray-500">
                      <Zap className="w-4 h-4 text-orange-500" />
                      Pressure tactics analysis
                    </li>
                  </ul>
                </div>
              </div>
              <img 
                src={IMAGES.shadow} 
                alt="Covert operations" 
                className="w-full h-48 object-cover mt-6 opacity-60 group-hover:opacity-80 transition-opacity"
              />
            </div>

            {/* Service Card 4 */}
            <div className="service-card group" data-testid="service-card-4">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-orange-500/10 flex-shrink-0 flex items-center justify-center">
                  <ShieldAlert className="w-10 h-10 text-orange-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 
                    className="text-2xl font-bold uppercase tracking-tight text-white mb-3"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    Social Engineering
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    We exploit procedural ambiguities and human psychology to test if guards 
                    are truly empowered to make critical security decisions.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-xs text-gray-500">
                      <Zap className="w-4 h-4 text-orange-500" />
                      Tailgating attempts
                    </li>
                    <li className="flex items-center gap-2 text-xs text-gray-500">
                      <Zap className="w-4 h-4 text-orange-500" />
                      Pretexting scenarios
                    </li>
                    <li className="flex items-center gap-2 text-xs text-gray-500">
                      <Zap className="w-4 h-4 text-orange-500" />
                      Badge cloning tests
                    </li>
                  </ul>
                </div>
              </div>
              <img 
                src={IMAGES.door} 
                alt="Secure door access" 
                className="w-full h-48 object-cover mt-6 opacity-60 group-hover:opacity-80 transition-opacity"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section id="results" className="py-24 bg-[#0A0A0A]" data-testid="results-section">
        <div className="section-container">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p 
                className="text-orange-500 text-xs tracking-[0.3em] uppercase mb-4"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                What You Receive
              </p>
              <h2 
                className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                data-testid="results-headline"
              >
                Actionable Intelligence
              </h2>
              <p className="text-gray-400 text-base md:text-lg mb-8">
                Our audit results aren't just a list of failures. You receive a comprehensive, 
                actionable intelligence report with everything needed to fortify your most 
                critical asset—your people.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500/10 flex-shrink-0 flex items-center justify-center">
                    <Video className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 
                      className="text-lg font-bold uppercase text-white mb-1"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                    >
                      Video Evidence
                    </h4>
                    <p className="text-gray-400 text-sm">
                      Documented footage of all penetration attempts and security responses.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500/10 flex-shrink-0 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 
                      className="text-lg font-bold uppercase text-white mb-1"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                    >
                      Detailed Analysis
                    </h4>
                    <p className="text-gray-400 text-sm">
                      Complete breakdown of vulnerabilities with risk assessment scoring.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500/10 flex-shrink-0 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 
                      className="text-lg font-bold uppercase text-white mb-1"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                    >
                      Clear Roadmap
                    </h4>
                    <p className="text-gray-400 text-sm">
                      Prioritized recommendations to strengthen your security posture.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src={IMAGES.tactical} 
                alt="Tactical operations" 
                className="w-full h-[500px] object-cover opacity-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
              <div className="absolute bottom-8 left-8 right-8 bg-black/80 border border-white/10 p-6">
                <p 
                  className="text-xs text-orange-500 tracking-[0.2em] uppercase mb-2"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  Due Diligence
                </p>
                <p className="text-gray-300 text-sm">
                  Demonstrate to insurers and stakeholders that your security investment 
                  delivers real-world protection.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Jobs Section */}
      <section id="careers" className="py-24 bg-[#0A0A0A]" data-testid="careers-section">
        <div className="section-container">
          <div className="text-center mb-16">
            <p 
              className="text-orange-500 text-xs tracking-[0.3em] uppercase mb-4"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              Join Our Team
            </p>
            <h2 
              className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Career Opportunities
            </h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              Join elite security professionals protecting high-value assets worldwide. 
              We offer positions from entry-level to executive leadership.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Featured Job - Venezuela */}
            <Link 
              to="/jobs/country-director-venezuela" 
              className="service-card group cursor-pointer hover:border-orange-500/50 transition-all"
              data-testid="job-card-venezuela"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-red-500/20 border border-red-500/50 text-red-400 text-[10px] px-2 py-1 uppercase tracking-wider">
                  High Threat
                </span>
              </div>
              <h3 
                className="text-lg font-bold uppercase tracking-tight text-white mb-2 group-hover:text-orange-500 transition-colors"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Country Director – Venezuela
              </h3>
              <p className="text-gray-400 text-xs mb-4">
                Operational command of high-threat security operations.
              </p>
              <p className="text-orange-500 font-bold text-sm">$410,000/year</p>
            </Link>

            {/* Ukraine - Extraction */}
            <Link 
              to="/jobs/extraction-agents-ukraine" 
              className="service-card group cursor-pointer hover:border-orange-500/50 transition-all"
              data-testid="job-card-ukraine"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-red-500/20 border border-red-500/50 text-red-400 text-[10px] px-2 py-1 uppercase tracking-wider">
                  Warzone
                </span>
              </div>
              <h3 
                className="text-lg font-bold uppercase tracking-tight text-white mb-2 group-hover:text-orange-500 transition-colors"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Extraction Agents – Ukraine
              </h3>
              <p className="text-gray-400 text-xs mb-4">
                Covert extraction operations in active conflict zone.
              </p>
              <p className="text-orange-500 font-bold text-sm">$800-$1,500/day</p>
            </Link>

            {/* Guatemala - EP Team */}
            <Link 
              to="/jobs/executive-protection-guatemala" 
              className="service-card group cursor-pointer hover:border-orange-500/50 transition-all"
              data-testid="job-card-guatemala"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-blue-500/20 border border-blue-500/50 text-blue-400 text-[10px] px-2 py-1 uppercase tracking-wider">
                  U.S. Only
                </span>
              </div>
              <h3 
                className="text-lg font-bold uppercase tracking-tight text-white mb-2 group-hover:text-orange-500 transition-colors"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                EP Team – Guatemala
              </h3>
              <p className="text-gray-400 text-xs mb-4">
                30-day covert protection assignment.
              </p>
              <p className="text-orange-500 font-bold text-sm">$600/day + Expenses</p>
            </Link>

            {/* Philippines - EP Detail */}
            <Link 
              to="/jobs/executive-protection-philippines" 
              className="service-card group cursor-pointer hover:border-orange-500/50 transition-all"
              data-testid="job-card-philippines"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-green-500/20 border border-green-500/50 text-green-400 text-[10px] px-2 py-1 uppercase tracking-wider">
                  Long-Term
                </span>
              </div>
              <h3 
                className="text-lg font-bold uppercase tracking-tight text-white mb-2 group-hover:text-orange-500 transition-colors"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                EP Detail – Philippines
              </h3>
              <p className="text-gray-400 text-xs mb-4">
                Large-scale executive protection details.
              </p>
              <p className="text-orange-500 font-bold text-sm">$400-$700/day</p>
            </Link>
          </div>

          <div className="text-center">
            <Link 
              to="/jobs" 
              className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-400 transition-colors uppercase tracking-wider text-sm"
              data-testid="view-all-jobs"
            >
              View All Positions
              <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-[#121212]" data-testid="contact-section">
        <div className="section-container">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <p 
                className="text-orange-500 text-xs tracking-[0.3em] uppercase mb-4"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                Get Started
              </p>
              <h2 
                className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                data-testid="contact-headline"
              >
                Request An Audit
              </h2>
              <p className="text-gray-400 text-base md:text-lg mb-8">
                Stop guessing about the effectiveness of your security. In an age of constant 
                threat, you can't afford to have a weak link. Let us find it for you, before 
                someone with malicious intent does.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <Mail className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                  <span className="text-gray-300 text-sm">Rrojas@simonadvancedsecurity.com</span>
                </div>
                <div className="flex items-center gap-4">
                  <Phone className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                  <span className="text-gray-300 text-sm">800-824-1906</span>
                </div>
                <div className="flex items-center gap-4">
                  <MapPin className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                  <span className="text-gray-300 text-sm">Nationwide Service Available</span>
                </div>
              </div>

              <div className="bg-black/40 border border-white/10 p-6">
                <p className="text-xs text-orange-500 tracking-[0.2em] uppercase mb-2">
                  Confidential
                </p>
                <p className="text-gray-400 text-sm">
                  All consultations and audit results are treated with the highest level of 
                  confidentiality. NDAs available upon request.
                </p>
              </div>
            </div>

            <div className="bg-black/40 border border-white/10 p-8">
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="space-y-6">
                  <div>
                    <label 
                      className="block text-xs text-gray-500 uppercase tracking-widest mb-2"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="contact-input"
                      placeholder="Your full name"
                      required
                      data-testid="input-name"
                    />
                  </div>

                  <div>
                    <label 
                      className="block text-xs text-gray-500 uppercase tracking-widest mb-2"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      Company *
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="contact-input"
                      placeholder="Organization name"
                      required
                      data-testid="input-company"
                    />
                  </div>

                  <div>
                    <label 
                      className="block text-xs text-gray-500 uppercase tracking-widest mb-2"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="contact-input"
                      placeholder="your@email.com"
                      required
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <label 
                      className="block text-xs text-gray-500 uppercase tracking-widest mb-2"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="contact-input"
                      placeholder="+1 (555) 000-0000"
                      data-testid="input-phone"
                    />
                  </div>

                  <div>
                    <label 
                      className="block text-xs text-gray-500 uppercase tracking-widest mb-2"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      Security Concern *
                    </label>
                    <textarea
                      name="security_concern"
                      value={formData.security_concern}
                      onChange={handleInputChange}
                      className="contact-input min-h-[120px] resize-none"
                      placeholder="Describe your security concerns or what you'd like us to test..."
                      required
                      data-testid="input-concern"
                    />
                  </div>

                  {submitStatus === "success" && (
                    <div className="bg-green-500/10 border border-green-500/30 p-4" data-testid="success-message">
                      <p className="text-green-500 text-sm">
                        Transmission received. Our team will contact you within 24 hours.
                      </p>
                    </div>
                  )}

                  {submitStatus === "error" && (
                    <div className="bg-red-500/10 border border-red-500/30 p-4" data-testid="error-message">
                      <p className="text-red-500 text-sm">
                        Transmission failed. Please try again or contact us directly.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="spinner" />
                        Transmitting...
                      </>
                    ) : (
                      <>
                        <ShieldAlert className="w-5 h-5" />
                        Secure Transmission
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0A0A0A] border-t border-white/10">
        <div className="section-container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <ShieldAlert className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
              <span 
                className="font-bold text-lg tracking-tight uppercase text-white"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Simon Advanced Security
              </span>
            </div>
            <p className="text-gray-500 text-xs tracking-widest">
              © {new Date().getFullYear()} ALL RIGHTS RESERVED
            </p>
            <div className="flex items-center gap-6">
              <Link to="/jobs" className="footer-link" data-testid="footer-careers">Careers</Link>
              <a href="#" className="footer-link" data-testid="footer-privacy">Privacy Policy</a>
              <a href="#" className="footer-link" data-testid="footer-terms">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/jobs/country-director-venezuela" element={<JobPosting />} />
        <Route path="/jobs/executive-protection-guatemala" element={<JobGuatemala />} />
        <Route path="/jobs/extraction-agents-ukraine" element={<JobUkraine />} />
        <Route path="/jobs/executive-protection-philippines" element={<JobPhilippines />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
